import type { ResponseReadAll } from "epsm-api-types";
import { Converter } from "epsm-api-types/converter";
import {
	type Security,
	type SecurityDTO,
	SecurityTypes,
} from "epsm-api-types/models";
import ExpiryMap from "expiry-map";
import { pMemoizeDecorator } from "p-memoize";
import type { LibrarySymbolInfo } from "@/charting_library/charting_library";
import { concatData } from "@/hooks/useGetData";
import { ONE_HOUR_IN_MS } from "@/lib/utils";
import { BaseService } from "@/services/BaseService";
import { HttpClient, type IHttpClient } from "@/services/HttpClient";
import type { IReadAllParams } from "@/services/RequestParams";
import { sortAndFilterSecurities } from "./helpers/sortAndFilterSecurities";

const cache = new ExpiryMap(8 * ONE_HOUR_IN_MS);

export class SecuritiesService extends BaseService<Security, SecurityDTO> {
	private constructor(private httpClient: IHttpClient) {
		super(new Converter(SecurityTypes));
	}

	private static instance: SecuritiesService;

	static create() {
		if (!SecuritiesService.instance) {
			SecuritiesService.instance = new SecuritiesService(HttpClient.create());
		}
		return SecuritiesService.instance;
	}

	@pMemoizeDecorator({ cache, cacheKey: (_) => "getSymbols" })
	async getSymbols(): Promise<LibrarySymbolInfo[]> {
		const securities = await this.fetchData();
		return securities.map(
			(item) =>
				({
					name: `${item.listing_exchange_mic}:${item.name}`,
					ticker: item.ticker,
					description: item.name,
					exchange: item.listing_exchange_mic,
					listed_exchange: item.listing_exchange_mic,
					type: item.code,
					session: "24x7",
					timezone: "America/New_York",
					minmov: 1,
					pricescale: 100,
					format: "price",
					has_daily: true,
					has_weekly_and_monthly: false,
					has_intraday: true,
					has_seconds: false,
					has_ticks: false,
				}) as LibrarySymbolInfo,
		);
	}

	private async fetchPage({ params }: { params: IReadAllParams }) {
		const response = await this.httpClient.get<ResponseReadAll<SecurityDTO>>(
			"/securities",
			{
				params: this.getQueryParams(params),
			},
		);
		return this.getResponseReadAll(response);
	}

	@pMemoizeDecorator({ cache, cacheKey: (_) => "fetchData" })
	private async fetchData(): Promise<Security[]> {
		const defaultParams: Readonly<IReadAllParams> = {
			fields: [
				"ticker",
				"name",
				"code",
				"primary_listing",
				"etf",
				"top_competitors",
				"stock_exchange_id",
				"type",
				"listing_exchange_mic",
			],
			sort: [{ name: "ticker", order: 1 }],
		};
		return await concatData(this.fetchPage.bind(this), {
			params: defaultParams,
		});
	}

	async getData(
		text: string,
		offset: number,
		limit: number,
	): Promise<ResponseReadAll<Security>> {
		const data = await this.fetchData();
		const result = sortAndFilterSecurities(data, text, offset, limit);

		return {
			data: result.slice(0, limit),
			metadata: {
				pagination: { nextPage: result.length > limit, offset, limit },
			},
		};
	}
}
